// Shared form-field markup (label, helper text, control, error), identical to the
// Ember I80::Form::Field, with the id/for/aria-describedby wiring done here.
import { h, nextId } from "./base.js";
import { icon } from "./icons.js";

/** Attributes copied from the <i80-*> tag to a native control that the element creates. */
export const FORWARDED = ["name", "value", "placeholder", "required", "disabled", "readonly", "autocomplete",
  "minlength", "maxlength", "pattern", "min", "max", "step", "inputmode", "form", "list", "autofocus", "rows", "cols",
  "spellcheck", "multiple", "size"];

export function forward(host, control, extra = []) {
  for (const name of [...FORWARDED, ...extra]) {
    if (host.hasAttribute(name)) control.setAttribute(name, host.getAttribute(name));
  }
}

/** The Required badge / (Optional) note that follows a label or a legend. */
export function indicator(host) {
  if (host.flag("required")) {
    return [
      " ",
      h("div", { class: "i80-badge i80-badge--size-small i80-badge--type-filled i80-badge--color-neutral i80-form-indicator", "aria-hidden": "true" },
        h("div", { class: "i80-badge__text" }, "Required")),
    ];
  }
  if (host.flag("optional")) return [h("span", { class: "i80-form-indicator i80-form-indicator--optional" }, "(Optional)")];
  return [];
}

export const ERROR_MESSAGE_CLASS = "i80-text i80-typography-body-100 i80-font-weight-medium i80-form-error__message";

/** A string becomes a message paragraph; an element the author wrote is styled as one. */
function asMessage(message) {
  if (typeof message === "string") return h("p", { class: ERROR_MESSAGE_CLASS }, message);
  if (!message.classList.contains("i80-form-error__message")) {
    message.className = [ERROR_MESSAGE_CLASS, message.getAttribute("class")].filter(Boolean).join(" ");
  }
  return message;
}

/**
 * The inside of an error block: the icon and one paragraph per message. Shared by the
 * fields, the groups and <i80-form-error>, so all three render identical markup.
 */
export function errorParts(messages) {
  return [
    icon("alert-diamond-fill", { className: "i80-form-error__icon" }),
    h("div", { class: "i80-form-error__content" }, ...messages.map(asMessage)),
  ];
}

/**
 * Build the field around `control` (once), keeping the same control element on updates so
 * typed values, focus and author attributes survive. Returns an update function.
 */
export function field(host, control, { layout = "vertical", controlWrapper = null } = {}) {
  // A clone of a rendered field carries the original's generated id; give it its own.
  // Author ids (e.g. Django's id_email) are never changed.
  let id = control.id;
  const generated = id.startsWith(`${host.localName}-`);
  if (!id || (generated && document.querySelectorAll(`#${CSS.escape(id)}`).length > 1)) id = nextId(host.localName);
  control.id = id;
  const root = host.part("field") ?? h("div", { "data-i80-part": "field" });
  const controlBox = root.querySelector(":scope > .i80-form-field__control") ?? h("div", { class: "i80-form-field__control" });
  if (!controlBox.contains(control)) controlBox.append(controlWrapper ?? control);
  if (!root.isConnected) host.append(root);

  return function render() {
    const label = host.getAttribute("label");
    const helper = host.getAttribute("helper-text");
    const error = host.getAttribute("error");
    const invalid = host.flag("invalid") || !!error;
    const helperId = `helper-text-${id}`;
    const errorId = `error-${id}`;

    // In a group (fieldset) each field also gets the group's contextual class.
    const inGroup = host.parentElement?.closest("i80-checkbox-group, i80-radio-group, i80-toggle-group, i80-form-fieldset");
    root.className = [`i80-form-field--layout-${layout}`, inGroup && "i80-form-group__control-field"].filter(Boolean).join(" ");
    const parts = [];
    if (label) {
      parts.push(h("label", { class: "i80-form-label i80-form-field__label", for: id, id: `label-${id}` }, label, ...indicator(host)));
    }
    const helperEl = helper ? h("div", { class: "i80-form-helper-text i80-form-field__helper-text", id: helperId }, helper) : null;
    if (helperEl && layout !== "flag") parts.push(helperEl);
    parts.push(controlBox);
    if (helperEl && layout === "flag") parts.push(helperEl);
    if (error) parts.push(h("div", { class: "i80-form-error i80-form-field__error", id: errorId }, ...errorParts([error])));
    root.replaceChildren(...parts);

    const describedBy = [helper && helperId, error && errorId, host.getAttribute("aria-describedby")].filter(Boolean);
    if (describedBy.length) control.setAttribute("aria-describedby", describedBy.join(" "));
    else control.removeAttribute("aria-describedby");
    if (invalid) control.setAttribute("aria-invalid", "true");
    else control.removeAttribute("aria-invalid");
    if (!label && host.hasAttribute("aria-label")) control.setAttribute("aria-label", host.getAttribute("aria-label"));
    return { invalid };
  };
}
